<template>
  <div class="flex gap-2">
    <div class="p-4 flex flex-col justify-between w-70 rounded-3xl bg-key">
      <div class="flex-1">
        <div class="flex items-center gap-1">
          <Icon
            icon="Technology-Wireframe-RTTriggerRT"
            class="size-5"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
          />
          <span
            class="text-m-medium transition-colors"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            >{{ t('itinerary.rtSensitivityTrigger.triggerSensitivity') }}</span
          >
          <Tooltip
            :content="t('itinerary.rtSensitivityTrigger.triggerTooltip')"
            side="bottom"
            align="start"
            :disabled="disabled"
          >
            <Icon
              icon="Function-Wireframe-Caution"
              class="w-4 h-4"
              :class="`${disabled ? 'text-neutral-dark-disabled' : 'text-text-glass-default'}`"
            />
          </Tooltip>
        </div>
        <div class="mt-4 flex items-center gap-0.5">
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleTriggerStepDown"
          >
            <Icon icon="Direction-Wireframe-Left2" :class="{ 'text-text-disabled': disabled }" />
          </Button>
          <div class="flex items-center gap-3" :class="{ 'opacity-25': disabled }">
            <span class="text-s-medium text-black-white">{{ minDisplayMm }}</span>
            <div class="relative">
              <Slider
                v-model="triggerDisplay"
                :min="minDisplayMm"
                :max="maxDisplayMm"
                :step="precisionStep"
                :disabled="disabled"
                thumb-alignment="overflow"
                class="h-3 w-38"
                track-class="h-3 bg-linear-[var(--color-rt2)]"
                range-class="bg-transparent"
                thumb-class="bg-transparent w-3.5 h-5 rounded-none"
                @value-commit="commitTrigger"
              >
                <template #thumb>
                  <div class="relative flex items-center w-full h-full">
                    <Icon icon="Direction-FIll-SwipeLR1" class="w-3.5 h-3.5 text-black" />
                    <div class="absolute h-5 w-px bg-black left-[50%]" />
                  </div>
                </template>
              </Slider>
              <div
                class="absolute top-4 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: triggerBubbleLeft }"
              >
                <div class="text-black-white text-s-medium">{{ formatMm(triggerDisplay[0]) }}</div>
              </div>
            </div>
            <span class="text-s-medium text-black-white">{{ maxDisplayMm }}</span>
          </div>
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleTriggerStepUp"
          >
            <Icon
              icon="Direction-Wireframe-Right2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
        </div>
      </div>
      <div class="relative h-8 shrink-0 flex items-center mb-1">
        <div class="h-px w-full bg-divider" />
        <div class="absolute left-1/2 -translate-x-1/2">
          <Tooltip
            :content="
              isRelated
                ? t('itinerary.rtSensitivityTrigger.linkedMode')
                : t('itinerary.rtSensitivityTrigger.unlinkedMode')
            "
            side="bottom"
            align="start"
          >
            <Button
              color="glass"
              shape="circle"
              size="icon-sm"
              :disabled="disabled || !relatedToggleEnabled"
              @click="isRelated = !isRelated"
            >
              <Icon
                :icon="isRelated ? 'Function-Wireframe-Link' : 'Function-Wireframe-Unlink'"
                class="w-5.5 h-5.5"
              />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div class="flex-1">
        <div class="flex items-center gap-1">
          <Icon
            icon="Technology-Wireframe-RTTriggerRT"
            class="size-5"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
          />
          <span
            class="text-m-medium transition-colors"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            >{{ t('itinerary.rtSensitivityTrigger.resetSensitivity') }}</span
          >
          <Tooltip
            :content="t('itinerary.rtSensitivityTrigger.resetTooltip')"
            side="bottom"
            align="start"
            :disabled="disabled"
          >
            <Icon
              icon="Function-Wireframe-Caution"
              class="w-4 h-4"
              :class="`${disabled ? 'text-neutral-dark-disabled' : 'text-text-glass-default'}`"
            />
          </Tooltip>
        </div>
        <div class="mt-4 flex items-center gap-0.5">
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleResetStepDown"
          >
            <Icon
              icon="Direction-Wireframe-Left2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
          <div class="flex items-center gap-3" :class="{ 'opacity-25': disabled }">
            <span class="text-s-medium text-black-white">{{ minDisplayMm }}</span>
            <div class="relative">
              <Slider
                v-model="resetDisplay"
                :min="minDisplayMm"
                :max="maxDisplayMm"
                :step="precisionStep"
                :disabled="disabled"
                thumb-alignment="overflow"
                class="h-3 w-38"
                track-class="h-3 bg-linear-[var(--color-rt2)]"
                range-class="bg-transparent"
                thumb-class="bg-transparent w-3.5 h-5 rounded-none"
                @value-commit="commitReset"
              >
                <template #thumb>
                  <div class="relative flex items-center w-full h-full">
                    <Icon icon="Direction-FIll-SwipeLR1" class="w-3.5 h-3.5 text-black" />
                    <div class="absolute h-5 w-px bg-black left-[50%]" />
                  </div>
                </template>
              </Slider>
              <div
                class="absolute top-4 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: resetBubbleLeft }"
              >
                <div class="text-black-white text-s-medium">{{ formatMm(resetDisplay[0]) }}</div>
              </div>
            </div>
            <span class="text-s-medium text-black-white">{{ maxDisplayMm }}</span>
          </div>
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleResetStepUp"
          >
            <Icon
              icon="Direction-Wireframe-Right2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
        </div>
      </div>
    </div>
    <div class="p-4 w-35.25 flex flex-col rounded-3xl bg-key">
      <div class="flex items-center gap-1 h-5.5 transition-colors">
        <Icon
          icon="Tab-Wireframe-Stroke"
          class="size-5"
          :class="precisionDisabled ? 'text-text-disabled' : 'text-black-white'"
        />
        <span
          class="text-m-medium"
          :class="precisionDisabled ? 'text-text-disabled' : 'text-black-white'"
        >
          {{ t('itinerary.rtSensitivityTrigger.precisionAdjust') }}
        </span>
      </div>
      <div
        class="mt-2 flex-1 relative flex flex-col rounded-lg bg-neutral-light-default1 overflow-hidden"
        :class="{ 'opacity-25': precisionDisabled }"
      >
        <div
          class="absolute left-0 right-0 flex-1 h-11 rounded-lg bg-white transition-all duration-300 ease-in-out"
          :style="{ top: `${rtPrecisionIndex * 44}px` }"
        />
        <button
          v-for="(label, index) in precisionLabels"
          :key="label"
          type="button"
          :disabled="precisionDisabled"
          class="relative z-1 h-11 text-m-medium rounded-lg text-center transition-colors duration-300"
          :class="precisionOptionClass(index)"
          @click="handlePrecisionChange(index as RtPrecisionIndex)"
        >
          {{ label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Icon, Button, Slider, Tooltip } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'
  type RtPrecisionIndex = 0 | 1 | 2 | 3

  const MIN_MM = 0.1
  const MAX_MM = 3.3

  const isRelated = defineModel<boolean>('isRelated', { required: true })

  const props = defineProps<{
    rtTriggerSensitivity: number
    rtResetSensitivity: number
    sensitivityEnabled: boolean
    relatedToggleEnabled: boolean
    precisionEnabled: boolean
    rtPrecisionIndex: number
    precisionStep: number
    precisionLabels: string[]
  }>()

  const emit = defineEmits<{
    'update:rtTriggerSensitivity': [value: number]
    'update:rtResetSensitivity': [value: number]
    'change-precision': [index: RtPrecisionIndex]
  }>()

  const { t } = useI18n()

  const disabled = computed(() => !props.sensitivityEnabled)
  const precisionDisabled = computed(() => !props.precisionEnabled)
  const precisionStep = computed(() => props.precisionStep)
  const minDisplayMm = MIN_MM
  const maxDisplayMm = MAX_MM

  const precisionOptionClass = (index: number) => {
    if (precisionDisabled.value) return 'cursor-not-allowed text-text-disabled'
    if (props.rtPrecisionIndex === index) return 'cursor-pointer text-black'
    return 'cursor-pointer text-black-white hover:bg-neutral-light-default1'
  }

  const handlePrecisionChange = (index: RtPrecisionIndex) => {
    if (precisionDisabled.value) return
    emit('change-precision', index)
  }

  const minRtSteps = computed(() => Math.round(MIN_MM / precisionStep.value))
  const maxRtSteps = computed(() => Math.round(MAX_MM / precisionStep.value))

  const formatDecimals = computed(() =>
    precisionStep.value >= 0.1 ? 1 : precisionStep.value >= 0.01 ? 2 : 3,
  )

  const formatValue = (value: number) => value.toFixed(formatDecimals.value)

  const formatMm = (value: number) => `${formatValue(value)}mm`

  const stepsToMm = (steps: number) => steps * precisionStep.value
  const mmToSteps = (mm: number) => Math.round(mm / precisionStep.value)

  const triggerDisplay = ref([stepsToMm(props.rtTriggerSensitivity)])
  watch(
    () => [props.rtTriggerSensitivity, props.precisionStep] as const,
    ([steps, step]) => {
      triggerDisplay.value = [steps * step]
    },
  )

  const commitTrigger = (val: number[]) => {
    if (disabled.value) return
    emit('update:rtTriggerSensitivity', mmToSteps(val[0]))
  }

  const resetDisplay = ref([stepsToMm(props.rtResetSensitivity)])
  watch(
    () => [props.rtResetSensitivity, props.precisionStep] as const,
    ([steps, step]) => {
      resetDisplay.value = [steps * step]
    },
  )

  const commitReset = (val: number[]) => {
    if (disabled.value) return
    emit('update:rtResetSensitivity', mmToSteps(val[0]))
  }

  const getBubbleLeft = (value: number) => {
    const ratio = (value - MIN_MM) / (MAX_MM - MIN_MM)
    const progress = Math.min(1, Math.max(0, ratio))
    return `${progress * 100}%`
  }

  const triggerBubbleLeft = computed(() => getBubbleLeft(triggerDisplay.value[0]))
  const resetBubbleLeft = computed(() => getBubbleLeft(resetDisplay.value[0]))

  const handleTriggerStepUp = () => {
    if (disabled.value) return
    emit('update:rtTriggerSensitivity', Math.min(maxRtSteps.value, props.rtTriggerSensitivity + 1))
  }

  const handleTriggerStepDown = () => {
    if (disabled.value) return
    emit('update:rtTriggerSensitivity', Math.max(minRtSteps.value, props.rtTriggerSensitivity - 1))
  }

  const handleResetStepUp = () => {
    if (disabled.value) return
    emit('update:rtResetSensitivity', Math.min(maxRtSteps.value, props.rtResetSensitivity + 1))
  }

  const handleResetStepDown = () => {
    if (disabled.value) return
    emit('update:rtResetSensitivity', Math.max(minRtSteps.value, props.rtResetSensitivity - 1))
  }
</script>

<style scoped></style>
